import {
  CompoundQuestion,
  CompoundQuestionDeploymentStatus,
  CompoundQuestionTemplateResponse,
  CompoundQuestionUpdateRequest,
  GetCompoundQuestionsRequest,
  GetCompoundQuestionsResponse,
} from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CompoundQuestionsAPI } from "api";

const queryKeys = {
  all: () => ["compound-questions"] as const,
  lists: () => [...queryKeys.all(), "list"] as const,
  list: (args: GetCompoundQuestionsRequest) => [...queryKeys.lists(), args] as const,
  get: (id: number) => [...queryKeys.all(), id] as const,
  deploymentStatus: (id: number) => [...queryKeys.all(), id, "deployment-status"] as const,
};

export const useQueryCompoundQuestions = <T = GetCompoundQuestionsResponse>(
  args: GetCompoundQuestionsRequest,
  options: UseQueryOptions<GetCompoundQuestionsResponse, unknown, T> = {},
) =>
  useQuery<GetCompoundQuestionsResponse, unknown, T>(
    queryKeys.list(args),
    () => CompoundQuestionsAPI.list(args),
    options,
  );

export const useQueryCompoundQuestion = <T = CompoundQuestion>(
  id: number,
  options: UseQueryOptions<CompoundQuestion, unknown, T> = {},
) => useQuery<CompoundQuestion, unknown, T>(queryKeys.get(id), () => CompoundQuestionsAPI.get(id), options);

export const useQueryCompoundQuestionDeploymentStatus = (
  id: number,
  options: UseQueryOptions<CompoundQuestionDeploymentStatus, unknown, CompoundQuestionDeploymentStatus> = {},
) =>
  useQuery<CompoundQuestionDeploymentStatus, unknown, CompoundQuestionDeploymentStatus>(
    queryKeys.deploymentStatus(id),
    () => CompoundQuestionsAPI.getDeploymentStatus(id),
    options,
  );

export const useCompoundQuestionUpdate = (
  id: number,
  options: UseMutationOptions<CompoundQuestion, unknown, CompoundQuestionUpdateRequest> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation((formData) => CompoundQuestionsAPI.update(id, formData), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.get(id));
      queryClient.invalidateQueries(queryKeys.lists());
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useCompoundQuestionDelete = (options: UseMutationOptions<void, unknown, number> = {}) => {
  const queryClient = useQueryClient();

  return useMutation((id) => CompoundQuestionsAPI.delete(id), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.lists());
      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useCompoundQuestionTemplateValidate = (
  options: UseMutationOptions<CompoundQuestionTemplateResponse, unknown, File> = {},
) => useMutation((file) => CompoundQuestionsAPI.validateTemplate(file), options);

export const useCompoundQuestionCreateFromTemplate = (
  options: UseMutationOptions<CompoundQuestionTemplateResponse, unknown, File> = {},
) => {
  const queryClient = useQueryClient();

  return useMutation((file) => CompoundQuestionsAPI.createFromTemplate(file), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(queryKeys.lists());
      options?.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to download the blank compound question template (.xlsx).
 */
export const useCompoundQuestionTemplateDownload = (options: UseMutationOptions<void, unknown, void> = {}) =>
  useMutation(() => CompoundQuestionsAPI.downloadTemplate(), options);
